import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import EmojiPicker from "emoji-picker-react";
import { Smile, Clapperboard, Send, X, Image as ImageIcon, Video, Loader2, Play } from "lucide-react";
import GifPicker from "@/components/GifPicker";
import MentionTextarea from "@/components/MentionTextarea";
import api from "@/lib/api";
import { uploadVideoChunked } from "@/lib/uploadVideo";

const MAX_IMAGES = 4;
const MAX_VIDEOS = 3;

/**
 * Compact composer for comments/replies: an auto-growing text area on top with the
 * emoji / GIF / photo / video / Post controls in a row underneath.
 * Calls onSubmit({ body, gif, images, videos }) and resets on success.
 */
export default function MiniComposer({ onSubmit, placeholder = "Write a reply…", testidPrefix = "mini", accent = "#ff4500", autoFocus = false, showVideo = true }) {
    const [body, setBody] = useState("");
    const [gif, setGif] = useState(null);
    const [images, setImages] = useState([]);
    const [videos, setVideos] = useState([]);
    const [showGif, setShowGif] = useState(false);
    const [showEmoji, setShowEmoji] = useState(false);
    const [sending, setSending] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [videoUploading, setVideoUploading] = useState(false);
    const fileRef = useRef(null);
    const videoRef = useRef(null);
    const rootRef = useRef(null);

    // Click outside closes the emoji / GIF pickers.
    useEffect(() => {
        const onDoc = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) { setShowEmoji(false); setShowGif(false); } };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);

    const canSend = !!(body.trim() || gif || images.length || videos.length);

    const onPickFiles = async (e) => {
        const files = Array.from(e.target.files || []);
        e.target.value = "";
        if (!files.length) return;
        const room = MAX_IMAGES - images.length;
        if (room <= 0) { toast.error(`Up to ${MAX_IMAGES} photos`); return; }
        setUploading(true);
        try {
            const uploaded = [];
            for (const f of files.slice(0, room)) {
                const fd = new FormData();
                fd.append("file", f);
                const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
                uploaded.push(data.url);
            }
            setImages((prev) => [...prev, ...uploaded]);
            setGif(null);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Upload failed");
        } finally { setUploading(false); }
    };

    const videoDuration = (file) => new Promise((resolve) => {
        try {
            const url = URL.createObjectURL(file);
            const v = document.createElement("video");
            v.preload = "metadata";
            v.onloadedmetadata = () => { URL.revokeObjectURL(url); resolve(v.duration || 0); };
            v.onerror = () => { URL.revokeObjectURL(url); resolve(0); };
            v.src = url;
        } catch { resolve(0); }
    });

    const onPickVideos = async (e) => {
        const files = Array.from(e.target.files || []);
        e.target.value = "";
        if (!files.length) return;
        const room = MAX_VIDEOS - videos.length;
        if (room <= 0) { toast.error(`Up to ${MAX_VIDEOS} videos`); return; }
        setVideoUploading(true);
        try {
            const uploaded = [];
            for (const f of files.slice(0, room)) {
                const dur = await videoDuration(f);
                if (dur && dur > 31) { toast.error(`"${f.name}" is longer than 30 seconds — trim it first`); continue; }
                const data = await uploadVideoChunked(f);
                uploaded.push({ url: data.url, poster: data.poster });
            }
            if (uploaded.length) { setVideos((prev) => [...prev, ...uploaded]); setGif(null); }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Video upload failed");
        } finally { setVideoUploading(false); }
    };

    const submit = async (e) => {
        e?.preventDefault?.();
        if (!canSend || sending) return;
        setSending(true);
        try {
            await onSubmit({ body: body.trim(), gif: gif || null, images, videos });
            setBody(""); setGif(null); setImages([]); setVideos([]); setShowGif(false); setShowEmoji(false);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not send");
        } finally { setSending(false); }
    };

    return (
        <div className="pt-1" data-testid={testidPrefix} ref={rootRef}>
            {images.length > 0 && (
                <div className="grid grid-cols-2 gap-1.5 mb-2" data-testid={`${testidPrefix}-images`}>
                    {images.map((url, i) => (
                        <div key={i} className="relative">
                            <img src={url} alt="" className="w-full max-h-40 object-contain rounded-lg bg-[#0a0a0a]" />
                            <button type="button" onClick={() => setImages((p) => p.filter((_, idx) => idx !== i))}
                                className="absolute top-1 right-1 flex h-5 w-5 items-center justify-center rounded-full bg-black/70 text-white hover:bg-black">
                                <X className="h-3 w-3" />
                            </button>
                        </div>
                    ))}
                </div>
            )}
            {videos.length > 0 && (
                <div className="grid grid-cols-3 gap-1.5 mb-2" data-testid={`${testidPrefix}-videos`}>
                    {videos.map((v, i) => (
                        <div key={i} className="relative aspect-video">
                            {v.poster ? <img src={v.poster} alt="" className="h-full w-full object-cover rounded-lg bg-black" /> : <div className="h-full w-full rounded-lg bg-black" />}
                            <span className="pointer-events-none absolute inset-0 flex items-center justify-center"><span className="flex h-7 w-7 items-center justify-center rounded-full bg-black/60"><Play className="h-3.5 w-3.5 text-white" fill="white" /></span></span>
                            <button type="button" onClick={() => setVideos((p) => p.filter((_, idx) => idx !== i))}
                                className="absolute top-1 right-1 flex h-5 w-5 items-center justify-center rounded-full bg-black/70 text-white hover:bg-black">
                                <X className="h-3 w-3" />
                            </button>
                        </div>
                    ))}
                </div>
            )}
            {gif && (
                <div className="relative w-fit mb-2">
                    <img src={gif.full_url} alt="GIF" className="max-h-40 rounded-lg" data-testid={`${testidPrefix}-gif-preview`} />
                    <button type="button" onClick={() => setGif(null)} data-testid={`${testidPrefix}-remove-gif`}
                        className="absolute top-1 right-1 flex h-5 w-5 items-center justify-center rounded-full bg-black/70 text-white hover:bg-black">
                        <X className="h-3 w-3" />
                    </button>
                </div>
            )}
            <form onSubmit={submit} className="relative">
                <MentionTextarea
                    as="textarea"
                    autoGrow
                    rows={1}
                    autoFocus={autoFocus}
                    wrapperClass="relative w-full"
                    testid={`${testidPrefix}-input`}
                    value={body}
                    onChange={setBody}
                    placeholder={placeholder}
                    maxLength={2000}
                    className="w-full min-h-9 resize-none overflow-hidden bg-[#0f0f0f] border border-[#2a2a2a] px-3 py-2 text-sm text-white outline-none focus:border-[#ff4500] leading-snug"
                />
                <div className="mt-2 flex items-center gap-1">
                    <button type="button" title="Emoji" data-testid={`${testidPrefix}-emoji-btn`} onClick={() => { setShowEmoji((s) => !s); setShowGif(false); }}
                        className="flex h-9 w-9 items-center justify-center rounded-full hover:bg-white/5" style={{ color: accent }}>
                        <Smile className="h-[18px] w-[18px]" />
                    </button>
                    <button type="button" title="GIF" data-testid={`${testidPrefix}-gif-btn`} onClick={() => { setShowGif((s) => !s); setShowEmoji(false); }} disabled={images.length > 0}
                        className="flex h-9 w-9 items-center justify-center rounded-full hover:bg-white/5 disabled:opacity-40" style={{ color: accent }}>
                        <Clapperboard className="h-[18px] w-[18px]" />
                    </button>
                    <button type="button" title="Photo" data-testid={`${testidPrefix}-image-btn`} onClick={() => fileRef.current?.click()} disabled={!!gif || uploading}
                        className="flex h-9 w-9 items-center justify-center rounded-full hover:bg-white/5 disabled:opacity-40" style={{ color: accent }}>
                        <ImageIcon className="h-[18px] w-[18px]" />
                    </button>
                    {showVideo && (
                        <button type="button" title="Video (30s max)" data-testid={`${testidPrefix}-video-btn`} onClick={() => videoRef.current?.click()} disabled={!!gif || videoUploading || videos.length >= MAX_VIDEOS}
                            className="flex h-9 w-9 items-center justify-center rounded-full hover:bg-white/5 disabled:opacity-40" style={{ color: accent }}>
                            {videoUploading ? <Loader2 className="h-[18px] w-[18px] animate-spin" /> : <Video className="h-[18px] w-[18px]" />}
                        </button>
                    )}
                    <input ref={fileRef} type="file" accept="image/*" multiple onChange={onPickFiles} className="hidden" data-testid={`${testidPrefix}-file`} />
                    <input ref={videoRef} type="file" accept="video/*" multiple onChange={onPickVideos} className="hidden" data-testid={`${testidPrefix}-video-file`} />
                    <button type="submit" disabled={!canSend || sending || uploading || videoUploading} data-testid={`${testidPrefix}-submit`}
                        className="ml-auto h-9 px-4 text-white uppercase tracking-[0.15em] text-[10px] font-semibold disabled:opacity-40 inline-flex items-center gap-1.5" style={{ backgroundColor: accent }}>
                        <Send className="h-3 w-3" /> {uploading || videoUploading ? "Uploading…" : "Post"}
                    </button>
                </div>
                {showGif && <GifPicker accent={accent} onClose={() => setShowGif(false)} onChoose={(g) => { setGif(g); setImages([]); setVideos([]); setShowGif(false); }} />}
                {showEmoji && (
                    <div className="absolute z-30 top-10 left-0" data-testid={`${testidPrefix}-emoji-picker`}>
                        <div className="relative">
                            <button type="button" onClick={() => setShowEmoji(false)} data-testid={`${testidPrefix}-emoji-close`}
                                className="absolute -top-2 -right-2 z-10 flex h-6 w-6 items-center justify-center rounded-full bg-[#0f0f0f] border border-[#2a2a2a] text-white hover:bg-black">
                                <X className="h-3.5 w-3.5" />
                            </button>
                            <EmojiPicker theme="dark" width={280} height={340} onEmojiClick={(ed) => setBody((b) => b + ed.emoji)} />
                        </div>
                    </div>
                )}
            </form>
        </div>
    );
}
